import { useState } from "react";
import { Siren, Shield, Heart, Phone, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

/**
 * SOSButton — fixed FAB visible across the driver dashboard.
 * On press, opens an emergency action sheet with 3 channels:
 *   • Polícia (190)  – tel: link + records alert in /api/driver/sos
 *   • SAMU    (192)  – tel: link + records alert
 *   • Contato        – dials a pre-saved emergency contact
 *
 * The backend captures geolocation so admins can dispatch help.
 * Localized strings are intentionally short for legibility on small screens.
 */
export default function SOSButton({ driverId, driverName, variant = "floating" }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sendAlert = async (type) => {
    setSubmitting(true);
    try {
      // Get current location (best-effort; SOS still fires even if denied)
      const coords = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 }
        );
      });

      if (!coords) {
        toast.error("Permita acesso à localização para o SOS funcionar.");
        setSubmitting(false);
        return;
      }

      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/driver/sos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type,
          latitude: coords.latitude,
          longitude: coords.longitude,
          message: type === "contact" ? `Acionou contato de emergência` : undefined,
        }),
      });
      if (r.ok) {
        toast.success("Alerta enviado à central. Mantenha-se seguro.");
        // Trigger the tel: shortcut for the chosen channel
        if (type === "police") window.location.href = "tel:190";
        else if (type === "samu") window.location.href = "tel:192";
        // For "contact" we don't auto-dial — driver will use their own contact app
      } else {
        toast.error("Falha ao enviar alerta. Ligue diretamente.");
      }
    } catch {
      toast.error("Erro de rede — ligue 190/192 diretamente.");
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };

  return (
    <>
      {variant === "pill" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Botão de emergência SOS"
          className="h-10 px-3 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 shadow-lg shadow-red-900/40 ring-2 ring-red-500/40 flex items-center gap-1.5 text-white font-extrabold text-xs tracking-widest"
          data-testid="sos-button"
        >
          <Siren className="w-4 h-4" />
          SOS
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Botão de emergência SOS"
          className="fixed bottom-24 right-4 z-[80] w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 shadow-2xl shadow-red-900/60 ring-4 ring-red-600/30 flex items-center justify-center text-white animate-pulse-slow"
          data-testid="sos-button"
        >
          <Siren className="w-7 h-7" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[9000] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !submitting && setOpen(false)}
          data-testid="sos-modal"
        >
          <div
            className="w-full max-w-sm bg-gradient-to-b from-zinc-900 to-zinc-950 border border-red-500/30 rounded-3xl shadow-2xl shadow-red-900/40 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-rose-600 px-5 py-3 flex items-center justify-between text-white">
              <div className="flex items-center gap-2 font-extrabold tracking-wider">
                <Siren className="w-5 h-5" /> EMERGÊNCIA
              </div>
              <button
                onClick={() => !submitting && setOpen(false)}
                aria-label="Fechar"
                className="text-white/80 hover:text-white"
                data-testid="sos-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2.5">
              <p className="text-xs text-zinc-400 text-center mb-2">
                Sua localização será enviada à central em tempo real.
              </p>
              <SosOption
                icon={Shield}
                label="Polícia"
                phone="190"
                color="from-blue-500 to-blue-700"
                onClick={() => sendAlert("police")}
                disabled={submitting}
                testId="sos-police"
              />
              <SosOption
                icon={Heart}
                label="SAMU"
                phone="192"
                color="from-emerald-500 to-emerald-700"
                onClick={() => sendAlert("samu")}
                disabled={submitting}
                testId="sos-samu"
              />
              <SosOption
                icon={Phone}
                label="Contato de emergência"
                phone="Notificar admin GoFood"
                color="from-amber-500 to-orange-600"
                onClick={() => sendAlert("contact")}
                disabled={submitting}
                testId="sos-contact"
              />
              {submitting && (
                <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Enviando alerta...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SosOption({ icon: Icon, label, phone, color, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r ${color} text-white font-extrabold text-left disabled:opacity-50 active:scale-[0.98] transition-transform`}
      data-testid={testId}
    >
      <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-base">{label}</p>
        <p className="text-[11px] font-bold text-white/80">{phone}</p>
      </div>
    </button>
  );
}
